import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsAPI } from '@/utils/api';
import useAppStore from '@/store/appStore';

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const response = await accountsAPI.getAll();
      return response.data;
    },
  });
}

export function useAccount(id) {
  return useQuery({
    queryKey: ['accounts', id],
    queryFn: async () => {
      const response = await accountsAPI.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: async (data) => {
      const response = await accountsAPI.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      addNotification({
        type: 'success',
        title: 'Account Created',
        message: 'Email account created successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Create Account',
        message: error.response?.data?.error?.message || error.message,
      });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: ({ id, data }) => accountsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      addNotification({
        type: 'success',
        title: 'Account Updated',
        message: 'Email account updated successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Update Account',
        message: error.message,
      });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (id) => accountsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      addNotification({
        type: 'success',
        title: 'Account Deleted',
        message: 'Email account deleted successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Delete Account',
        message: error.message,
      });
    },
  });
}

export function useTestAccount() {
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: async (data) => {
      const response = await accountsAPI.test(data);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.success) {
        addNotification({
          type: 'success',
          title: 'Connection Successful',
          message: 'IMAP connection test passed',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Connection Failed',
          message: data?.error || 'IMAP connection test failed',
        });
      }
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Connection Test Failed',
        message: error.response?.data?.error?.message || error.message,
      });
    },
  });
}

export function useSyncAccount() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (id) => accountsAPI.sync(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      addNotification({
        type: 'info',
        title: 'Sync Started',
        message: 'Email sync initiated',
      });
    },
    onError: (error, accountId) => {
      // Check if this is a Microsoft authentication error
      const isMicrosoftAuthError = error.message?.includes('AUTHENTICATE failed');

      // Get account info to check if it's a Microsoft account
      const accounts = queryClient.getQueryData(['accounts']);
      const account = accounts?.find(a => a.id === accountId);
      const isMicrosoftAccount = account?.email?.match(/@(hotmail|outlook|live)\.(com|net|org)/i);

      if (isMicrosoftAuthError && isMicrosoftAccount) {
        addNotification({
          type: 'error',
          title: 'Microsoft Authentication Failed',
          message: 'Hotmail/Outlook requires an app-specific password. Generate one at account.microsoft.com/security → App passwords, then update your account password.',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Sync Failed',
          message: error.message,
        });
      }
    },
  });
}
